import Papa from 'papaparse';
import { priceStringAsNumber } from '../../utils/format-money';
import { PriceListCsvError } from './errors';

type CsvRow = {
  sku?: string;
  price?: string;
  qty?: string;
  price_list_slug?: string;
  unit_of_measure?: string;
};

type ParseResult = {
  data: CsvRow[];
  meta: Papa.ParseMeta;
};

const INVALID_MIME_TYPES = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel spreadsheet (.xlsx)',
  // 'application/vnd.ms-excel': 'Excel spreadsheet (.xls)', @see https://bugzilla.mozilla.org/show_bug.cgi?id=1934918
  'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument Spreadsheet',
} as const;

const BINARY_CHECK_CHUNK_SIZE = 4096;

// Checks the first 4KB for any characters that signify the file being binary
async function isLikelyBinary(file: File) {
  const firstChunk = file.slice(0, BINARY_CHECK_CHUNK_SIZE);
  const text = await firstChunk.text();

  // Check for:
  // 1. \uFFFD (The "replacement character"  generated when decoding binary as text)
  // 2. \u0000 (The NULL byte, common in binary files like Excel/Images)
  return text.includes('\uFFFD') || text.includes('\u0000');
}

async function parseFileAsync(file: File): Promise<ParseResult> {
  // Check for invalid file before parsing - error on binary files
  if (await isLikelyBinary(file)) {
    throw new PriceListCsvError([
      'File is not a valid CSV. It appears to be a binary file (e.g. Excel). Please save as CSV.',
    ]);
  }

  return new Promise((resolve, reject) => {
    Papa.parse<CsvRow>(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.toLowerCase().trim(), // Ignore case in headers

      complete: resolve,
      error: reject,
    });
  });
}

export async function validatePriceList(file: File, hasPriceListSlug?: boolean): Promise<string[]> {
  if (!file) {
    return ['Please select a file to upload.'];
  }

  // MIME Type Validation - reject known binary types
  if (Object.keys(INVALID_MIME_TYPES).includes(file.type)) {
    const friendlyName = INVALID_MIME_TYPES[file.type as keyof typeof INVALID_MIME_TYPES];
    return [`Invalid file type: ${friendlyName}. Please save as CSV.`];
  }

  try {
    const { data, meta } = await parseFileAsync(file);
    const errors: string[] = [];
    const missingHeaders: string[] = [];

    // Check missing headers
    if (!meta.fields?.includes('sku')) {
      missingHeaders.push('sku');
    }

    if (!meta.fields?.includes('price')) {
      missingHeaders.push('price');
    }

    if (hasPriceListSlug && !meta.fields?.includes('price_list_slug')) {
      missingHeaders.push('price_list_slug');
    }

    if (missingHeaders.length > 0) {
      return [`Missing required columns: ${missingHeaders.join(', ')}.`];
    }

    if (data.length === 0) {
      return ['The file appears to be empty.'];
    }

    // Validate rows
    let lineIndex = 1;
    for (const row of data) {
      lineIndex++;
      const lineErrors: string[] = [];

      const sku = row.sku?.trim();
      const priceRaw = row.price?.trim();
      const priceList = row.price_list_slug?.trim();

      // Check missing values
      if (!sku) {
        lineErrors.push(`Line ${lineIndex}: SKU is missing.`);
      }

      if (!priceRaw) {
        lineErrors.push(`Line ${lineIndex}: Price is missing.`);
      } else {
        try {
          priceStringAsNumber(priceRaw);
        } catch (err) {
          const message = (err as { message: string }).message;
          lineErrors.push(`Line ${lineIndex}: ${message}.`);
        }
      }

      // Check price list slug (if required)
      if (hasPriceListSlug && !priceList) {
        lineErrors.push(`Line ${lineIndex}: Price list slug is missing.`);
      }

      if (lineErrors.length) {
        // If errors exist with this list, skip...
        errors.push(...lineErrors);
      }
    }

    return errors;
  } catch (err) {
    if (err instanceof PriceListCsvError) {
      return err.errors;
    }

    throw err;
  }
}
